import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { IDatasetTypeMetadata } from 'app/extensibility/metadatas/resource-type/dataset-type-metadata';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { onetimeQueryTemplate } from 'app/registries/linked-service/dynamics-metadata';
import { writeBehaviorAllOptions, writeBehaviorOptionValues } from 'app/shared/constants/write-behavior.const';
import { dynamicsAlternateKeyMetadata, maxConcurrentConnectionsMetadata } from '../consts/metadata-consts';
import { getUpsertOption } from 'app/registries/consts/copy-job-const';

const dynamicsSinkWriteBehaviors = [writeBehaviorOptionValues.upsert];

function getCopySinkMetadata(): ControlMetadata[] {
    return [
        {
            type: 'parameterizable-dropdown',
            bind: 'writeBehavior',
            label: stringResources.ModelName_WriteBehavior,
            isLabelVertical: false,
            options: writeBehaviorAllOptions
                .filter((option) => dynamicsSinkWriteBehaviors.indexOf(option.value) >= 0)
        },
        dynamicsAlternateKeyMetadata,
        {
            type: 'parameterizable',
            child: {
                type: 'checkbox',
                bind: 'ignoreNullValues',
                label: stringResources.ModelName_IgnoreNullValues,
                description: stringResources.IgnoreNullValuesDescription
            },
            dynamicContentTextClass: 'input-spaced'
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textbox-number',
                bind: 'writeBatchSize',
                label: stringResources.ModelName_WriteBatchSize,
                inputType: 'number',
                defaultValue: 10,
                min: 0
            },
            dynamicContentTextClass: 'input-spaced'
        },
        maxConcurrentConnectionsMetadata,
        {
            type: 'parameterizable-dropdown',
            bind: 'bypassBusinessLogicExecution',
            label: stringResources.bypassBusinessLogicExecution,
            description: stringResources.bypassBusinessLogicExecutionDesc,
            allowClear: true,
            placeholderText: stringResources.SelectOrType,
            editable: true,
            options: [
                {
                    label: 'CustomSync',
                    value: 'CustomSync'
                },
                {
                    label: 'CustomAsync',
                    value: 'CustomAsync'
                },
                {
                    label: 'CustomSync,CustomAsync',
                    value: 'CustomSync,CustomAsync'
                }
            ]
        },
        {
            type: 'parameterizable',
            child: {
                type: 'checkbox',
                bind: 'bypassPowerAutomateFlows',
                label: stringResources.bypassPowerAutomateFlows
                // description: stringResources.convertDec2IntDesc, // TODO - desc needed
            },
            dynamicContentTextClass: 'input-spaced'
        }
    ];
}

export const dynamicsEntityMetadata: IDatasetTypeMetadata = {
    type: adfModelTypeMap.dynamics.datasetType,
    supportLinkedServiceTypes: [adfModelTypeMap.dynamics.linkedServiceType],
    displayName: stringResources.Dynamics_DisplayText,
    datasetKind: 'table',
    isSource: true,
    isSink: true,
    icon: {
        className: 'svg-i-100x100-dynamics_365'
    },
    notSupportTypeConversionWhenSource: true,
    notSupportPhysicalType: true,
    v1SupportSchema: true,
    supportCreatedAsV2: true,
    supportDataFlowSource: true,
    supportDataFlowSink: true,
    tableNameDisplay: stringResources.TableNameDisplay_EntityName,
    tableNamePlural: stringResources.Entities,
    isFirstClass: true,
    category: ['ServicesApps'],
    copySinkControls: getCopySinkMetadata(),
    dataflowSourceControls: [
        {
            type: 'parameterizable',
            child: {
                type: 'textbox-number',
                bind: 'timeout',
                label: stringResources.Timeout,
                isDataFlowExpression: true
            },
            dynamicContentTextClass: 'input-spaced',
            showErrorIcon: false,
            isDataFlowExpression: true
        },
        {
            type: 'radio-switch',
            bind: 'useQuery',
            label: stringResources.InputLabel,
            options: [
                {
                    label: stringResources.ModelName_Table,
                    value: false
                },
                {
                    label: stringResources.QueryLabel,
                    value: true
                }
            ],
            cases: [
                {
                    caseValue: true,
                    children: [
                        {
                            type: 'parameterizable',
                            child: {
                                type: 'textarea',
                                bind: 'query',
                                label: stringResources.QueryLabel,
                                placeholder: onetimeQueryTemplate,
                                isDataFlowExpression: true
                            },
                            dynamicContentTextClass: 'input-spaced',
                            showErrorIcon: false,
                            isDataFlowExpression: true
                        }
                    ]
                }
            ]
        }
    ],
    dataflowSinkControls: [
        {
            type: 'parameterizable',
            child: {
                type: 'textbox-number',
                bind: 'timeout',
                label: stringResources.Timeout,
                isDataFlowExpression: true
            },
            dynamicContentTextClass: 'input-spaced',
            showErrorIcon: false,
            isDataFlowExpression: true
        },
        {
            type: 'app-dynamic-dataflow-update-method',
            bind: {
                allowInsert: 'allowInsert',
                allowDelete: 'allowDelete',
                allowUpsert: 'allowUpsert',
                allowUpdate: 'allowUpdate'
            }
        },
        {
            type: 'app-dynamic-dataflow-browse-selection',
            bind: 'alternateKeyName',
            label: stringResources.ModelName_AlternateKeyName,
            optionBind: {
                label: 'alternateKey',
                value: 'alternateKey'
            },
            browseEntity: false,
            hide: `{ allowDelete.expression === 'false' && allowUpsert.expression === 'false' && allowUpdate.expression === 'false' }`
        }
    ],
    copyJob: {
        updateMethod: {
            supportUpdateMethod: true,
            options: [getUpsertOption()],
            updateMethodProperties: ['writeBehavior', 'alternateKeyName'],
            singleUpsertKeyColumn: true,
            upsertKeyColumnLabel: stringResources.ModelName_AlternateKey,
            upsertKeyColumnRequired: false
        }
    }
};
